import { Form } from '@/components/form/Form';
import FieldLabel from '@/components/form/fields/FieldLabel';
import { TextField } from '@/components/form/fields/text/TextInput';
import { Multistep } from '@/components/multistep/Multistep';
import { Step } from '@/components/multistep/Questionnaire';
import { Container } from '@/layouts/main/Main';
import { Address } from '@/services/api/commonTypes';
import { useDeleteLogo, useUploadLogo } from '@/services/api/hooks/images';
import { usePartner, useUpdatePartner } from '@/services/api/hooks/partners';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import AddressField from '../../components/form/fields/address/AddressField';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';
import { deserialiseAddress, serialiseAddress } from '../onboarding/commonSteps/addressHelper';
import EmployeeCountField from './EmployeeCountField';
import UploadMspLogoStep from './logo/UploadMspLogoStep';

interface EditMspWizardProps {}



const EditMSPFormFields = z.object({
  name: z.string().min(1, 'Required'),
  companyInfo: z.object({
    name: z.string().min(1, 'Required'),
    number: z.string().min(1, 'Required'),
    address: Address,
    employeeCount: z.number().min(1),
    vatNumber: z.string().min(1, 'Required')
  }),
  logo: z.any().optional(),
  allowCustomLogo: z.boolean(),
  enableCustomNotifications: z.boolean(),
  haloClientId: z.string().optional().nullable(),
  haloGeneralUserId: z.string().optional().nullable(),
  isProductToursEnabled: z.boolean().default(false)
});

export type EditMSPFormFields = z.infer<typeof EditMSPFormFields>;

const EditMspWizard: FunctionComponent<EditMspWizardProps> = () => {
  const { partnerId } = useParams<'partnerId'>();

  if (!partnerId) throw new Error('Missing partner id');

  const [partner] = usePartner(partnerId);

  const role = useRole();
  const navigate = useNavigate();

  const updatePartner = useUpdatePartner(partnerId);
  const uploadLogo = useUploadLogo();
  const deleteLogo = useDeleteLogo();

  const methods = useForm<EditMSPFormFields>({
    resolver: zodResolver(EditMSPFormFields),
    defaultValues: {
      name: partner.name,
      companyInfo: {
        name: partner.registeredInfo?.name || '',
        number: partner.registeredInfo?.number || '',
        address: deserialiseAddress(partner.registeredInfo?.address),
        employeeCount: partner.registeredInfo?.employeeCount ?? 1,
        vatNumber: partner.registeredInfo.vatNumber || ''
      },
      allowCustomLogo: partner.propertyBag.allowCustomLogo,
      enableCustomNotifications: partner.enableCustomNotifications ?? false,
      haloClientId: partner.haloClientId || '',
      haloGeneralUserId: partner.haloGeneralUserId || '',
      isProductToursEnabled: partner.isProductToursEnabled ?? false
    }
  });

  const onSubmit = useCallback(async () => {
    const data = methods.getValues();

    await updatePartner.mutateAsync({
      name: data.name,
      code: partner.code,
      regionId: partner.region.id,
      registeredInfo: {
        name: data.companyInfo.name,
        number: data.companyInfo.number,
        address: serialiseAddress(data.companyInfo.address),
        employeeCount: data.companyInfo.employeeCount,
        vatNumber: data.companyInfo.vatNumber
      },
      disabled: partner.disabled,
      allowCustomLogo: data.allowCustomLogo,
      enableCustomNotifications: data.enableCustomNotifications,
      haloClientId: data.haloClientId || null,
      haloGeneralUserId: data.haloGeneralUserId || null,
      isProductToursEnabled: data.isProductToursEnabled
    });

    if (data.logo) {
      await uploadLogo.mutateAsync({ file: data.logo, id: partnerId });
    } else if (data.logo === false) {
      await deleteLogo.mutateAsync({ id: partnerId });
    }

    if (isPartner) {
      navigate('/');
      toast(`MSP ${data.companyInfo.name} - settings successfully edited`);
    } else {
      navigate('/msps/manageMsps');
      toast(`MSP ${data.companyInfo.name} - successfully edited`);
    }
  }, [methods]);

  const getError = () => {
    const updatePartnerErrorObj = updatePartner.error as Error | undefined;
    const uploadLogoErrorObj = uploadLogo.error as Error | undefined;

    return updatePartnerErrorObj ? updatePartnerErrorObj.message : uploadLogoErrorObj ? uploadLogoErrorObj.message : '';
  };

  const error = getError();

  const isPartner = role.type === RoleType.Partner;

  return (
    <Container className="w-full py-8">
      <Form className="flex w-full flex-col py-3" methods={methods} onSubmit={() => {}}>
        <Multistep onFinish={onSubmit}>
          <Step
            id="companyInfo"
            title="Company Information"
            validate={async () =>
              await methods.trigger([
                'name',
                'companyInfo.name',
                'companyInfo.number',
                'companyInfo.employeeCount',
                'companyInfo.vatNumber'
              ])
            }
          >
            <div className="flex flex-row space-x-5">
              <TextField readonly={isPartner} className="flex-1" id="name" label="Name" />
              <TextField readonly={isPartner} id="companyInfo.vatNumber" label="VAT number" />
            </div>
            <div className="flex flex-row space-x-5">
              <TextField readonly={isPartner} className="flex-1" id="companyInfo.name" label="Company name" />
              <TextField readonly={isPartner} id="companyInfo.number" label="Registered company number" />
            </div>
            <EmployeeCountField />
            <div>
              <FieldLabel id={'region'} label={'Region'}></FieldLabel>
              <div className="text-sm py-2 pl-3">{partner.region?.name || 'Not specified'}</div>
            </div>
            {role.type === RoleType.Admin && (
              <>
                <div className="mt-4 flex items-center gap-3">
                  <FieldLabel id="enableCustomNotifications" label="Enable Custom Notifications" />
                  <Controller
                    name="enableCustomNotifications"
                    control={methods.control}
                    render={({ field }) => (
                      <ToggleInput id="enableCustomNotifications" value={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <FieldLabel id="isProductToursEnabled" label="Enable Product Tours" />
                  <Controller
                    name="isProductToursEnabled"
                    control={methods.control}
                    render={({ field }) => (
                      <ToggleInput id="isProductToursEnabled" value={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
                <div className="mt-4">
                  <TextField id="haloClientId" label="Halo Client ID" />
                </div>
                <div className="mt-4">
                  <TextField id="haloGeneralUserId" label="Halo General User ID" />
                </div>
              </>
            )}
          </Step>
          <Step
            id="businessAddress"
            title="Business Address"
            validate={async () =>
              await methods.trigger([
                'companyInfo.address.addressLine1',
                'companyInfo.address.country',
                'companyInfo.address.postalCode',
                'companyInfo.address.stateCounty',
                'companyInfo.address.townCity'
              ])
            }
          >
            <AddressField fieldKey="companyInfo" />
          </Step>
          <UploadMspLogoStep mspId={partnerId} />
        </Multistep>

        {error && <p className="text-red-500">{error}</p>}
      </Form>
    </Container>
  );
};

export default EditMspWizard;
